<template>
	<!-- eslint-disable vue/no-v-html -->
	<div
		v-tooltip="tooltipObj"
		class="icon-container"
		:name="computedName"
		:size="size"
		:disabled="disabled"
		:effects="effects && state !== 'wiggle'"
		:style="{ transform: `rotate(${rotate}deg)` }"
		:data-animate="animate"
		@click="handleClick"
	>
		<div class="icon" :type="type" :state="state" :data-color="color" v-html="svg" />
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { VTooltip } from "v-tooltip";
import colorNames from "../utils/ColorNames";
import iconDigest from "./icon-digest";

const TYPE = ["filled", "outlined"] as const;

const SIZE = [
	"xx-small",
	"x-small",
	"small",
	"medium",
	"large",
	"x-large",
	"xx-large",
	"xxx-large",
	"20px",
	"18px",
	"36px",
	"10px"
] as const;

const STATE = [
	"font-light",
	"light",
	"dark",
	"default",
	"primary",
	"secondary",
	"success",
	"error",
	"warning",
	"white",
	"black",
	"root",
	"wiggle",
	"element",
	"element-light",
	"element-dark"
] as const;

export type IconState = typeof STATE[number];

export type IconType = typeof TYPE[number];
export type IconSize = typeof SIZE[number];
export type IconColor = typeof colorNames[number];

/**
 * Icons are symbols that visually communicate a message or action using a metaphor.
 * Icons often accompany the text to make it informative and easier to understand.
 */
export default defineComponent({
	name: "Icon",
	directives: {
		tooltip: VTooltip
	},
	props: {
		/**
		 * Required to locate svg file
		 */
		name: {
			type: String,
			required: true
		},
		/**
		 * 'filled' | 'outlined'
		 */
		type: {
			type: String as PropType<IconType>,
			default: "filled",
			validator(value: IconType) {
				return TYPE.includes(value);
			}
		},
		/**
		 * 'xx-small' | 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' | 'xxx-large' | '20px' | '18px' | '36px' | '10px'
		 */
		size: {
			type: String as PropType<IconSize>,
			default: "medium",
			validator(value: IconSize) {
				return SIZE.includes(value);
			}
		},

		/**
		 * true | false
		 * <br/> Used to display icon in disable state
		 */
		disabled: {
			type: Boolean,
			default: false
		},

		/**
		 * Used to rotate icon
		 */
		rotate: {
			type: Number,
			default: 0
		},

		/**
		 * 'gray-100' | 'gray-200' | 'gray-300' | 'gray-400' | 'gray-500' | 'gray-600' | 'gray-700' | 'gray-800' | 'gray-900' | 'primary-100' | 'primary-200' | 'primary-300' | 'primary-400' | 'primary-500' | 'secondary-100' | 'secondary-200' | 'secondary-300' | 'secondary-400' | 'secondary-500' | 'warning-100' | 'warning-200' | 'warning-300' | 'warning-400' | 'warning-500' | 'success-100' | 'success-200' | 'success-300' | 'success-400' | 'success-500' | 'danger-100' | 'danger-200' | 'danger-300' | 'danger-400' | 'danger-500' | 'color1-100' | 'color1-200' | 'color1-300' | 'color2-100' | 'color2-200' | 'color2-300' | 'color3-100' | 'color3-200' | 'color3-300' | 'color4-100' | 'color4-200' | 'color4-300' | 'color5-100' | 'color5-200' | 'color5-300' | 'color6-100' | 'color6-200' | 'color6-300'
		 */
		color: {
			type: String as PropType<IconColor>,
			default: null,
			validator(value: IconColor) {
				return colorNames.includes(value);
			}
		},

		/**
		 * 'light' | 'dark' | 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'white' | 'black' | 'font-light' | 'wiggle' | 'root' | 'element' | 'element-light' | 'element-dark'
		 */
		state: {
			type: String as PropType<IconState>,
			default: "default",
			validator(value: IconState) {
				return STATE.includes(value);
			}
		},

		/**
		 * true | false <br/> Used to enable/disable :hover, :active, :focus effects <br/> Default : true
		 */
		effects: {
			type: Boolean,
			default: true
		},

		animate: {
			type: Boolean,
			default: false
		},

		tooltip: {
			type: String,
			default: null
		}
	},
	computed: {
		tooltipObj() {
			if (this.tooltip) {
				return {
					content: `${this.tooltip}`,
					container: "body",
					classes: ["black"]
				};
			}

			return this.tooltip;
		},
		svg() {
			let svg = iconDigest["i-generic"];

			try {
				if (this.name.indexOf("<svg") > -1) {
					svg = this.name;
				} else if (this.name in iconDigest) {
					svg = iconDigest[this.name];
				} else {
					// eslint-disable-next-line no-console
					console.error(`[Icon] ${this.name} not found. Displaying i-generic `);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error(`[Icon] ${this.name} not found. Displaying i-generic `);
			}

			return svg;
		},
		computedName() {
			if (this.name.indexOf("<svg") > -1) {
				return "custom";
			} else {
				return this.name;
			}
		}
	},
	methods: {
		handleClick(event: MouseEvent) {
			if (!this.disabled) {
				/**
				 * Click event : triggered when icon is clicked
				 *
				 * @event click
				 */
				this.$emit("click", event);
			}
		}
	}
});
</script>
<style lang="scss">
@use "sass:map";
@import "../styles/theme-variables/color-names";

$sizes: "xxx-large", "xx-large", "x-large", "large", "medium", "small", "x-small", "xx-small",
	"20px", "18px", "36px", "10px";
$dimention: (
	"xxx-large": 54px,
	"xx-large": 48px,
	"x-large": 40px,
	"large": 32px,
	"medium": 24px,
	"small": 16px,
	"x-small": 12px,
	"xx-small": 8px,
	"20px": 20px,
	"36px": 36px,
	"18px": 18px,
	"10px": 10px
);

@mixin icon-mixin(
	$icon-colors,
	$icon-hover-color,
	$icon-focus-color,
	$icon-focus-background-color,
	$colors
) {
	div.icon-container {
		> div.icon {
			@each $state, $color in $icon-colors {
				&[type="outlined"][state="#{$state}"] svg {
					fill: none;
					stroke: $color;
					*[stroke^="white"] {
						stroke: $color;
					}
				}

				&[type="filled"][state="#{$state}"] svg {
					fill: $color;
					*[fill^="white"] {
						fill: $color;
					}
				}
			}
			@each $name, $color in $colors {
				&[type="outlined"][state="default"][data-color="#{$name}"] svg {
					fill: none;
					stroke: $color;
					*[stroke^="white"] {
						stroke: $color;
					}
				}

				&[type="filled"][state="default"][data-color="#{$name}"] svg {
					fill: $color;
					*[fill^="white"] {
						fill: $color;
					}
				}
			}
			&[state="wiggle"] {
				&:before {
					background-color: $icon-hover-color;
				}
			}
		}
		&:not([disabled]) {
			&[effects] {
				&:hover {
					& > [type="outlined"] > svg {
						stroke: $icon-hover-color;
						*[stroke^="white"] {
							stroke: $icon-hover-color;
						}
					}

					& > [type="filled"] > svg {
						fill: $icon-hover-color;
						*[fill^="white"] {
							fill: $icon-hover-color;
						}
					}
				}
				&:focus,
				&:active {
					& > [type="outlined"] > svg {
						stroke: $icon-focus-color;
					}

					& > [type="filled"] > svg {
						fill: $icon-focus-color;
					}
					&:before {
						background-color: $icon-focus-background-color;
					}
				}
			}
		}
	}
}

div.icon-container {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	position: relative;
	transform-origin: center;
	transition: all 0.3s linear;

	&[disabled] {
		cursor: not-allowed;
		svg {
			opacity: 0.2;
		}

		&:focus {
			outline: none;
		}
	}
	@each $size in $sizes {
		&[size="#{$size}"] {
			height: fit-content;
			width: fit-content;

			> div.icon {
				svg {
					width: map.get($dimention, $size);
					height: map.get($dimention, $size);
				}
			}
		}
	}

	> div.icon {
		// padding: 2px;
		display: flex;
		justify-content: center;
		align-items: center;
		transform-origin: center;
		transition: all 0.3s linear;
		width: auto;
		height: auto;
		z-index: 1;

		> svg {
			margin: unset;
			transition: fill 0.2s linear, stroke 0.2s linear;
		}
		&:hover {
			> svg {
				transition: fill 0.5s linear, stroke 0.5s linear;
			}
		}

		&[state="wiggle"] {
			cursor: pointer;
			animation: wiggle 0.4s infinite;
			transform-origin: left top;
			* {
				z-index: 2;
			}
			&:before {
				content: "";
				width: 100%;
				height: 100%;
				min-width: 24px;
				min-height: 24px;
				position: absolute;
				outline: none;
				transform: scale(1.5);
				transform-origin: center;
				z-index: 1;
			}
		}
	}
	&:not([disabled]) {
		&[effects] {
			cursor: pointer;
			&:focus,
			&:active {
				&:before {
					content: "";
					// left: 0;
					// top: 0;
					width: 100%;
					height: 100%;
					min-width: 24px;
					min-height: 24px;
					position: absolute;
					border-radius: 50%;
					outline: none;
					transform: scale(1.7);
					transform-origin: center;
				}
			}
		}
	}
	&[data-animate] {
		-webkit-animation: spin 2s linear infinite;
		-moz-animation: spin 2s linear infinite;
		animation: spin 2s linear infinite;
	}
	@-moz-keyframes spin {
		100% {
			-moz-transform: rotate(360deg);
		}
	}
	@-webkit-keyframes spin {
		100% {
			-webkit-transform: rotate(360deg);
		}
	}
	@keyframes spin {
		100% {
			-webkit-transform: rotate(360deg);
			transform: rotate(360deg);
		}
	}
	// &[name="loading"] > .icon {
	// 	animation: spin 2s linear infinite !important;
	// }

	// @keyframes spin {
	// 	100% {
	// 		transform: rotate(360deg);
	// 	}
	// }

	&[name="l-aws-small"] {
		svg {
			path:first-child {
				fill: var(--dashboard-font-dark) !important;
			}
		}
	}
}

$slideout-icon-colors: (
	"font-light": var(--slideout-font-light),
	"light": var(--slideout-icon-light),
	"dark": var(--slideout-icon-dark),
	"default": var(--slideout-icon),
	"element": var(--slideout-element),
	"element-light": var(--slideout-element-light),
	"element-dark": var(--slideout-element-dark),
	"primary": var(--slideout-primary),
	"secondary": var(--slideout-secondary),
	"success": var(--slideout-success),
	"error": var(--slideout-danger),
	"warning": var(--slideout-warning),
	"wiggle": var(--slideout-icon-dark),
	"root": var(--slideout-background),
	"white": #fff,
	"black": #000
);

//pass mode related variables
div.sideout-container {
	@include icon-mixin(
		$slideout-icon-colors,
		var(--slideout-primary),
		var(--slideout-icon-dark),
		var(--slideout-border),
		$color-names
	);
}

$dashboard-icon-colors: (
	"font-light": var(--dashboard-font-light),
	"light": var(--dashboard-icon-light),
	"dark": var(--dashboard-icon-dark),
	"default": var(--dashboard-icon),
	"element": var(--dashboard-element),
	"element-light": var(--dashboard-element-light),
	"element-dark": var(--dashboard-element-dark),
	"primary": var(--dashboard-primary),
	"secondary": var(--dashboard-secondary),
	"success": var(--dashboard-success),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning),
	"wiggle": var(--dashboard-icon-dark),
	"root": var(--dashboard-background),
	"white": #fff,
	"black": #000
);
@include icon-mixin(
	$dashboard-icon-colors,
	var(--dashboard-primary),
	var(--dashboard-icon-dark),
	var(--dashboard-border),
	$color-names
);

@keyframes wiggle {
	0% {
		transform: rotate(0deg);
	}
	30% {
		transform: rotate(-2.5deg);
	}
	60% {
		transform: rotate(2.5deg);
	}
	to {
		transform: rotate(0deg);
	}
}
</style>
